import { Skeleton, Tooltip, Typography } from '@mui/material';
import classnames from 'classnames';
import { HeaderMetaConfigurationProps } from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration.types';
import { ReactComponent as NexsetIcon } from 'new/components/icons/nexsetsOutline.svg';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { Stack } from 'new/elements/Stack';
import { TextField } from 'new/elements/TextField';
import { useHeaderMetaConfig } from 'new/components/flows/context/useHeaderMetaConfig';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { useGetMetaInfoByConnectorType } from 'new/hooks/useGetMetaInfoByConnectorType';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import css from './HeaderMetaConfiguration.module.scss';

function HeaderMetaConfiguration(props: HeaderMetaConfigurationProps) {
  const {
    name,
    description,
    connection,
    isDestination,
    enableInputFields = true,
    nexsetChipChildren = null,
    sourceOrDestinationChipChildren = null,
    nexsetId,
    dense,
  } = props;
  const [inputValues, setInputValues] = useState({ name, description });
  const meta = useGetMetaInfoByConnectorType(connection);

  // Sync name/description props → context so resource hooks can read them
  // even if the user never edits the fields (e.g. edit-mode initial values).
  const { setInputValues: setContextValues } = useHeaderMetaConfig(name, description);

  const onChangeDebounced = useDebouncedCallback(props?.onChange, [props?.onChange], 400);

  const onchange = (fieldName: string, value: string) => {
    onChangeDebounced({ name: fieldName, value });
    setInputValues((previous) => ({ ...previous, [fieldName]: value }));
    setContextValues({ [fieldName]: value });
  };

  const containerClassName = enableInputFields ? `${css.container} ${dense ? css.dense : ''}` : '';
  return (
    <div className={containerClassName}>
      <div
        className={classnames({
          [css.inner_container]: true,
          [css.inner_container_full_width]: enableInputFields,
        })}
      >
        <Stack direction="row" alignItems="center" gap="8px">
          <DestinationChip nexsetId={nexsetId} isDestination={isDestination}>
            {nexsetChipChildren}
          </DestinationChip>
          <div data-testid="meta-config-resource-chip" className={css.chip}>
            <ConnectionTypeChip
              icon={meta?.iconSm}
              iconOnly
              style={{ border: 'none', padding: 0 }}
            />
            <div className={css.logoSeparator} />
            <Typography variant="bodySmall" className={css.logoLabel}>
              {meta?.name}
            </Typography>
            {sourceOrDestinationChipChildren}
          </div>
        </Stack>

        {enableInputFields ? (
          <div className={css.meta_inputs_container}>
            <TextField
              name="name"
              inputProps={{ 'data-testid': 'input-resource-name' }}
              value={inputValues.name}
              onChange={(e) => onchange('name', e.target.value)}
              placeholder="Enter a name"
              label="Name"
              size="small"
              fullWidth={false}
              className={css.detailsInput}
              InputProps={{
                style: {
                  marginTop: 0,
                  flex: 1,
                  fontSize: '0.875rem',
                  lineHeight: '1.5rem',
                },
              }}
            />
            <TextField
              name="description"
              inputProps={{ 'data-testid': 'input-resource-description' }}
              value={inputValues.description}
              onChange={(e) => onchange('description', e.target.value)}
              placeholder="Optional"
              label="Description"
              size="small"
              fullWidth={false}
              className={css.detailsInput}
              InputProps={{
                style: {
                  marginTop: 0,
                  flex: 1,
                  fontSize: '0.875rem',
                  lineHeight: '1.5rem',
                },
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

HeaderMetaConfiguration.displayName = 'HeaderMetaConfiguration';

export default HeaderMetaConfiguration;

function DestinationChip({ nexsetId, isDestination, children }) {
  const nexsetName = useSelector((state: any) => state.entities.dataSets[nexsetId]?.name);

  if (!isDestination) {
    return null;
  }

  if (!nexsetName) {
    return (
      <>
        <Skeleton variant="text" width={40} height={30} />
        <div className={css.chip}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="rectangular" width={30} height={20} />
        </div>
        <Skeleton variant="text" width={30} height={30} />
      </>
    );
  }

  return (
    <>
      <Typography variant="bodyDefault">Send</Typography>
      <div className={css.chip}>
        <NexsetIcon />
        <div className={css.logoSeparator} />
        <Tooltip title={nexsetName}>
          <Typography variant="bodySmall" className={css.truncatedText}>
            {nexsetName}
          </Typography>
        </Tooltip>
        {children}
      </div>
      <Typography variant="bodyDefault">to</Typography>
    </>
  );
}
